import React, { useState, useEffect } from "react";
import CustomButton from "../Common/CustomButton";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { MixpanelService } from "../../utils/mixpanelService";
import { attachScenariosWithSuites } from "../../redux-store/testSuiteScenarioReducers/testSuiteScenarioActions";
import { getAllTestSuites } from "../../redux-store/testSuiteReducers/testSuiteActions";
import LoadingOverlay from "../Common/LoadingOverlay";

const AttachTestSuiteModal = ({ projectId, open, onClose, onCancel }) => {
  const dispatch = useDispatch();
  const selectedActiveTestScenarios = useSelector(
    (state) => state.testScenario.selectedTestScenarios.filter((test) => test.status === "active")
  );
  const allTestSuites = useSelector((state) => state.testSuite.testSuitesList);
  const isLoading = useSelector((state) => state.testSuite.isLoading);
  const [errors, setErrors] = useState({});
  const [testSuite, setTestSuite] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) MixpanelService.track("attach_test_suite_modal_opened", {});
  }, [open]);

  useEffect(() => {
    fetchAllTestSuites();
    validate(["scenario"]);
  }, []);

  const fetchAllTestSuites = async () => {
    await dispatch(
      getAllTestSuites({
        projectId: projectId,
        pageNumber: 1,
        pageSize: 100000,
      })
    );
  };

  const onTestSuiteChange = (event, testSuite) => {
    setTestSuite((prevState) => testSuite);
  };

  const handleClose = () => {
    MixpanelService.track("attach_test_suite_modal_closed", {});
    onClose?.();
  };

  const handleCancel = () => {
    MixpanelService.track("attach_test_suite_modal_closed", {});
    setTestSuite(null);
    setErrors({});
    onCancel?.();
  };

  const validate = (tests = ["scenario", "suite"]) => {
    let isValid = true;
    if (
      (!selectedActiveTestScenarios || selectedActiveTestScenarios?.length === 0) &&
      tests.indexOf("scenario") !== -1
    ) {
      setErrors({ scenario: "Please select atleast one active test scenario." });
      isValid = false;
    }
    if (!testSuite?.id && tests.indexOf("suite") !== -1) {
      setErrors({ suite: "Invalid test suite." });
      isValid = false;
    }
    return isValid;
  };

  const onAddConfirm = async () => {
    setIsSaving(true);
    if (!validate()) {
      setIsSaving(false);
      return;
    }
    await dispatch(
      attachScenariosWithSuites({
        testSuiteId: testSuite?.id,
        testScenarioIds: [
          ...selectedActiveTestScenarios
            .map((test) => test.id),
        ],
      })
    );
    MixpanelService.track("test_scenario_attached_to_suite", { test_suite_id: testSuite?.id });
    setTestSuite(null);
    setErrors({});
    setIsSaving(false);
    onCancel?.();
    window.open(`/test-suites/${testSuite?.id}/`, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="attach-test-suite-dialog-title"
    >
      <DialogTitle id="attach-test-suite-dialog-title" sx={{ fontSize: 20 }}>
        Add to existing test suite
      </DialogTitle>
      <DialogContent
        sx={{
          width: "500px",
          height: "auto",
          maxHeight: "500px",
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: "14px" }} className="text_caption_error">
          {errors.suite || errors.scenario}
        </span>
        <h5 style={{ marginTop: 16 }}>Select Test Suite</h5>
        {isLoading ? (
          <LoadingOverlay />
        ) : (
          <Autocomplete
            options={allTestSuites}
            getOptionLabel={(option) => option.title}
            onChange={onTestSuiteChange}
            value={testSuite}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ mt: 1 }}
                size="small"
                variant="outlined"
              />
            )}
          />
        )}
        <h5 style={{ marginTop: 32, marginBottom: 8 }}>
          Selected Test Sceanrios
        </h5>
        {selectedActiveTestScenarios
          .map((test) => (
            <Chip
              key={test.id}
              id={test.id}
              label={test.title}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        {
          !selectedActiveTestScenarios?.length &&
          <h6>None selected</h6>
        }
        <Alert severity="info" sx={{ mt: 2 }}>
          Only test scenarios with active status will be considered for test
          suite addition.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="error"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={onAddConfirm}
          color="primary"
          variant="contained"
          disabled={Boolean(!testSuite?.id) || !!errors.scenario}
        >
          Add test scenarios to test suite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachTestSuiteModal;
